import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../services/api';

const inquiryTypeLabels = {
  general: 'General Inquiry',
  product: 'Product Question',
  order: 'Order Support',
  returns: 'Returns & Exchanges',
  wholesale: 'Wholesale Inquiry',
  press: 'Press & Media',
};

const formatAddress = (address) => {
  if (!address) {
    return '—';
  }

  const parts = [
    address.line1,
    address.city,
    address.state,
    address.zipCode,
    address.country,
  ].filter(Boolean);

  return parts.length ? parts.join(', ') : '—';
};

const formatDateTime = (value) => {
  if (!value) {
    return '—';
  }

  try {
    return new Date(value).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      dateStyle: "short",
      timeStyle: "short"
    });
  } catch (error) {
    return value;
  }
};

const Inquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchInquiries();
  }, []);

  const fetchInquiries = async () => {
    try {
      setLoading(true);
      const response = await api.get('/contact');
      setInquiries(response.data?.inquiries || []);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInquiry = (inquiry) => {
    setSelectedInquiry(inquiry);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedInquiry(null);
  };

  const handleDeleteInquiry = async (inquiryId) => {
    const confirmed = window.confirm('Are you sure you want to delete this inquiry?');
    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(inquiryId);
      await api.delete(`/contact/${inquiryId}`);
      toast.success('Inquiry deleted successfully');
      await fetchInquiries();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete inquiry');
    } finally {
      setDeletingId(null);
    }
  };

  const renderTableBody = () => {
    if (inquiries.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={9} align="center">
            <Typography color="textSecondary">No inquiries found.</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return inquiries.map((inquiry) => {
      const userDetails = inquiry.userDetails || {};
      const displayName = userDetails.name || inquiry.name;
      const displayEmail = userDetails.email || inquiry.email;
      const displayPhone = userDetails.phone || '—';
      const displayAddress = formatAddress(userDetails.address);
      const messagePreview =
        inquiry.message && inquiry.message.length > 80
          ? `${inquiry.message.slice(0, 80)}…`
          : inquiry.message;

      return (
        <TableRow key={inquiry._id} hover>
          <TableCell>{displayName}</TableCell>
          <TableCell>{displayEmail}</TableCell>
          <TableCell>{displayPhone || '—'}</TableCell>
          <TableCell>{displayAddress}</TableCell>
          <TableCell>
            <Chip
              label={inquiryTypeLabels[inquiry.inquiryType] || inquiry.inquiryType}
              size="small"
              color="primary"
              variant="outlined"
            />
          </TableCell>
          <TableCell>{inquiry.subject}</TableCell>
          <TableCell sx={{ maxWidth: 260 }}>
            <Typography variant="body2" color="textSecondary">
              {messagePreview || '—'}
            </Typography>
          </TableCell>
          <TableCell>{formatDateTime(inquiry.createdAt)}</TableCell>
          <TableCell>
            <Stack direction="row" spacing={1}>
              <Tooltip title="View details">
                <span>
                  <IconButton size="small" onClick={() => handleViewInquiry(inquiry)}>
                    <VisibilityIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Delete inquiry">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteInquiry(inquiry._id)}
                    disabled={deletingId === inquiry._id}
                  >
                    {deletingId === inquiry._id ? (
                      <CircularProgress size={18} />
                    ) : (
                      <DeleteIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>
      );
    });
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Contact Inquiries</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchInquiries}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>Inquiry Type</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{renderTableBody()}</TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Inquiry Details</DialogTitle>
        <DialogContent dividers>
          {selectedInquiry && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Submitted By
                </Typography>
                <Typography variant="body1">
                  {selectedInquiry.userDetails?.name || selectedInquiry.name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedInquiry.userDetails?.email || selectedInquiry.email}
                </Typography>
                {(selectedInquiry.userDetails?.phone || selectedInquiry.user?.phone) && (
                  <Typography variant="body2" color="textSecondary">
                    {selectedInquiry.userDetails?.phone ||
                      selectedInquiry.user?.phone}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  {formatAddress(selectedInquiry.userDetails?.address)}
                </Typography>
              </Box>

              <Divider />

              <Stack direction="row" spacing={2}>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Inquiry Type
                  </Typography>
                  <Typography>{inquiryTypeLabels[selectedInquiry.inquiryType] || selectedInquiry.inquiryType}</Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Submitted At
                  </Typography>
                  <Typography>{formatDateTime(selectedInquiry.createdAt)}</Typography>
                </Box>
              </Stack>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Subject
                </Typography>
                <Typography>{selectedInquiry.subject}</Typography>
              </Box>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Message
                </Typography>
                <Typography sx={{ whiteSpace: 'pre-line' }}>
                  {selectedInquiry.message}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
          {selectedInquiry && (
            <Button
              color="error"
              onClick={() => handleDeleteInquiry(selectedInquiry._id)}
              disabled={deletingId === selectedInquiry._id}
            >
              {deletingId === selectedInquiry._id ? (
                <CircularProgress size={18} />
              ) : (
                'Delete'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Inquiries;

